import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { TenantBrandingPublic } from '@senviro/core-api-client';
import { apiFetch } from '@/lib/api-fetch';

/**
 * `useLoginBranding` (#1709) — resolve white-label branding for the
 * current host on the **pre-auth** login surface.
 *
 * Calls the public `tenant-branding/by-domain` proxy
 * (`apps/api/src/proxy-core/branding-by-domain.controller.ts`, `@Public()`)
 * with no bearer, so the login screen can render the partner's logo,
 * primary colour and product name *before* the user signs in.
 *
 * Resolution surface:
 *   - `undefined` while the lookup is in flight (login renders platform
 *     defaults immediately, then swaps in branding if/when it resolves).
 *   - `null` when the host has no registered partner branding — the
 *     endpoint's by-design "null = no match" contract (NOT an error), so
 *     the login page simply keeps the platform default.
 *   - a `TenantBrandingPublic` row otherwise.
 *
 * `retry: false` + a long stale time: branding rarely changes and a
 * pre-auth page must never spin on a missing/slow resolver.
 */
export function useLoginBranding(): UseQueryResult<TenantBrandingPublic | null> {
  const host =
    typeof window !== 'undefined' ? window.location.hostname : '';

  return useQuery<TenantBrandingPublic | null>({
    queryKey: ['login-branding', host] as const,
    enabled: host.length > 0,
    staleTime: 10 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      const raw = await apiFetch<TenantBrandingPublic | { data: null } | null>(
        `/api/v1/proxy/core/tenant-branding/by-domain?host=${encodeURIComponent(
          host,
        )}`,
      );
      // Mirror the null-on-empty unwrap used by `useIdentityProvider`:
      // the proxy may hand back `{ data: null }` for the no-match case.
      if (
        raw &&
        typeof raw === 'object' &&
        'data' in raw &&
        (raw as { data: unknown }).data === null
      ) {
        return null;
      }
      return (raw as TenantBrandingPublic | null) ?? null;
    },
  });
}
